import { ChallengeService, ChallengeServiceZod } from '@duck/api-definitions'
import { Button, Textarea } from '@duck/ui'
import { TranslationKey, apis, useTar } from '@/modules'
import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { RateSlider } from '@/components/RateSlider/RateSlider'

type SubmitUserFeedbackProps = {
    challengeId: string
    onCancel: () => void
    onSuccess: (feedback: ChallengeService.UserFeedback) => void
}

type FieldName =
    | 'overallRating'
    | 'practiceNextRating'
    | 'agreeWithFeedback'
    | 'whyNoAgree'
    | 'freeText'

type FormState = {
    overallRating?: number
    practiceNextRating?: number
    agreeWithFeedback: boolean
    whyNoAgree: string
    freeText: string
}

const overallRatingToLabelMap: Record<number, TranslationKey> = {
    1: 'challenges.overallRatingLabel1',
    2: 'challenges.overallRatingLabel2',
    3: 'challenges.overallRatingLabel3',
    4: 'challenges.overallRatingLabel4',
    5: 'challenges.overallRatingLabel5',
    6: 'challenges.overallRatingLabel6',
}

const practiceNextRatingToLabelMap: Record<number, TranslationKey> = {
    1: 'challenges.practiceNextRatingLabel1',
    2: 'challenges.practiceNextRatingLabel2',
    3: 'challenges.practiceNextRatingLabel3',
}

type FieldErrors = Partial<Record<FieldName, string>>

const userFeedbackSchema = ChallengeServiceZod.schemas.UserFeedback
const submitUserFeedbackSchema = ChallengeServiceZod.schemas.SubmitUserFeedbackRequest

const baseInputClasses =
    'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'

export const SubmitUserFeedback = ({
    challengeId,
    onCancel,
    onSuccess,
}: SubmitUserFeedbackProps) => {
    const { t } = useTar()
    const [formState, setFormState] = useState<FormState>({
        overallRating: undefined,
        practiceNextRating: undefined,
        agreeWithFeedback: true,
        whyNoAgree: '',
        freeText: '',
    })
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
    const [isSubmitting, setIsSubmitting] = useState(false)

    const updateField = (field: keyof FormState, value: string | boolean | number) => {
        setFormState((prev) => ({
            ...prev,
            [field]: value,
        }))
        setFieldErrors((prev) => {
            const updated = { ...prev }
            delete updated[field as FieldName]
            return updated
        })
    }

    const setValidationErrors = (errorMap: FieldErrors) => {
        setFieldErrors(errorMap)
        toast.error(t('userFeedback.validationError'))
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const rawPayload: unknown = {
            challengeId,
            userFeedback: {
                overallRating: formState.overallRating,
                practiceNextRating: formState.practiceNextRating,
                agreeWithFeedback: formState.agreeWithFeedback,
                whyNoAgree:
                    !formState.agreeWithFeedback && formState.whyNoAgree.trim() === ''
                        ? undefined
                        : formState.whyNoAgree.trim() || undefined,
                freeText: formState.freeText.trim() || undefined,
            },
        }

        const validationResult = submitUserFeedbackSchema.safeParse(rawPayload)

        if (!validationResult.success) {
            const newErrors: FieldErrors = {}
            validationResult.error.errors.forEach((error) => {
                const [root, field] = error.path
                if (root === 'userFeedback' && typeof field === 'string') {
                    if (
                        field === 'overallRating' ||
                        field === 'practiceNextRating' ||
                        field === 'agreeWithFeedback' ||
                        field === 'whyNoAgree' ||
                        field === 'freeText'
                    ) {
                        newErrors[field] = error.message
                    }
                }
            })
            setValidationErrors(newErrors)
            return
        }

        // Additional validation to guard against NaN values (Zod already handles this, but TypeScript assurance)
        const userFeedbackValidation = userFeedbackSchema.safeParse(
            validationResult.data.userFeedback
        )
        if (!userFeedbackValidation.success) {
            const newErrors: FieldErrors = {}
            userFeedbackValidation.error.errors.forEach((error) => {
                const [, field] = error.path
                if (typeof field === 'string') {
                    newErrors[field as FieldName] = error.message
                }
            })
            setValidationErrors(newErrors)
            return
        }

        setIsSubmitting(true)
        try {
            const response = await apis.challengeService.submitUserFeedback(validationResult.data)

            if (response.data.success) {
                toast.success(t('userFeedback.submitSuccess'))
                onSuccess(validationResult.data.userFeedback)
            } else {
                toast.error(response.data.message ?? t('userFeedback.submitFailed'))
            }
        } catch (error) {
            console.error('Failed to submit user feedback', error)
            toast.error(t('userFeedback.submitFailed'))
        } finally {
            setIsSubmitting(false)
        }
    }
    return (
        <form onSubmit={handleSubmit} className="flex h-full flex-col space-y-6">
            <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground">
                    {t('userFeedback.subtitle')}
                </h3>
                <p className="text-sm text-muted-foreground">
                    {t('userFeedback.description')}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                        {t('userFeedback.overallRatingLabel')}
                    </label>
                    <p className="text-xs text-muted-foreground h-4">
                        {formState.overallRating ? t(
                            overallRatingToLabelMap[
                            formState.overallRating
                            ]
                        ) : ''}
                    </p>
                    <RateSlider
                        totalPoints={6}
                        value={formState.overallRating}
                        onChange={(value) => updateField('overallRating', value)}
                        disabled={isSubmitting}
                    />
                    {fieldErrors.overallRating && (
                        <p className="mt-2 text-xs text-destructive">{fieldErrors.overallRating}</p>
                    )}
                </div>
                <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                        {t('userFeedback.practiceNextRatingLabel')}
                    </label>
                    <p className="text-xs text-muted-foreground h-4">
                        {formState.practiceNextRating ? t(
                            practiceNextRatingToLabelMap[
                            formState.practiceNextRating
                            ]
                        ) : ' '}
                    </p>
                    <RateSlider
                        totalPoints={3}
                        value={formState.practiceNextRating}
                        onChange={(value) => updateField('practiceNextRating', value)}
                        disabled={isSubmitting}
                    />
                    {fieldErrors.practiceNextRating && (
                        <p className="mt-2 text-xs text-destructive">
                            {fieldErrors.practiceNextRating}
                        </p>
                    )}
                </div>
            </div>

            <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                    {t('userFeedback.agreeWithFeedbackLabel')}
                </label>
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant={formState.agreeWithFeedback ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                            updateField('agreeWithFeedback', true)
                            setFieldErrors((prev) => {
                                if (!prev.whyNoAgree) {
                                    return prev
                                }
                                const { whyNoAgree, ...rest } = prev
                                return rest
                            })
                        }}
                        disabled={isSubmitting}
                    >
                        {t('common.yes')}
                    </Button>
                    <Button
                        type="button"
                        variant={!formState.agreeWithFeedback ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateField('agreeWithFeedback', false)}
                        disabled={isSubmitting}
                    >
                        {t('common.no')}
                    </Button>
                </div>
                {fieldErrors.agreeWithFeedback && (
                    <p className="text-xs text-destructive">{fieldErrors.agreeWithFeedback}</p>
                )}
            </div>

            {!formState.agreeWithFeedback && (
                <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                        {t('userFeedback.whyNoAgreeLabel')}
                    </label>
                    <input
                        type="text"
                        className={baseInputClasses}
                        value={formState.whyNoAgree}
                        onChange={(event) => updateField('whyNoAgree', event.target.value)}
                        disabled={isSubmitting}
                        placeholder={t('userFeedback.whyNoAgreePlaceholder')}
                    />
                    {fieldErrors.whyNoAgree && (
                        <p className="mt-1 text-xs text-destructive">{fieldErrors.whyNoAgree}</p>
                    )}
                </div>
            )}

            <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                    {t('userFeedback.freeTextLabel')}
                </label>
                <Textarea
                    className="min-h-[120px]"
                    value={formState.freeText}
                    onChange={(event) => updateField('freeText', event.target.value)}
                    placeholder={t('userFeedback.freeTextPlaceholder')}
                    disabled={isSubmitting}
                />
                {fieldErrors.freeText && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.freeText}</p>
                )}
            </div>

            <div className="mt-auto flex items-center justify-end gap-2 border-t border-border pt-4">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={isSubmitting}
                >
                    {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? t('common.submitting') : t('common.submit')}
                </Button>
            </div>
        </form>
    )
}

